import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Chip } from '@material-ui/core'
import dayjs from 'dayjs'

import useReservationsApi from '../../hooks/reservations/useReservationsApi'
import useAmenitiesApi from '../../hooks/amenities/useAmenitiesApi'
import CustomTable from '../customTable/CustomTable'
import ListHeader from '../common/ListHeader'
import DeleteConfirmationModal from '../common/DeleteConfirmationModal'
import { TextArea } from '../common/FormFields'

const STATUS_LABEL = {
  confirmed: 'Confirmada',
  cancelled: 'Cancelada',
  completed: 'Pasada',
  pending: 'Pendiente',
}

const showDevTools = process.env.NODE_ENV === 'development'

const isPast = (r) =>
  r.status === 'cancelled' || dayjs(r.endDatetime).isBefore(dayjs())

export default function ReservationList() {
  const classes = useStyles()
  const [lastParams, setLastParams] = useState({})
  const [useMock, setUseMock] = useState(false)
  const [pendingCancel, setPendingCancel] = useState(null)
  const [cancelReason, setCancelReason] = useState('')

  const [{ reservationsState }, methods] = useReservationsApi()
  const [{ amenityTypesState }, amenityMethods] = useAmenitiesApi()

  useEffect(() => {
    amenityMethods.getAmenityTypes()
  }, [])

  // DEV ONLY — remove before shipping
  const MOCK_RESERVATIONS = [
    { id: '1', startDatetime: dayjs().add(1, 'day').toISOString(), endDatetime: dayjs().add(1, 'day').add(1, 'hour').toISOString(), status: 'confirmed', amenity: { name: 'Pileta', deletedAt: null, amenityType: { icon: '🏊' }, building: { name: 'Torre Norte' } }, apartment: { name: '101' }, user: { name: 'Juan', surname: 'García' }, totalPrice: 25, currency: 'USD' },
    { id: '2', startDatetime: dayjs().add(2, 'day').toISOString(), endDatetime: dayjs().add(2, 'day').add(2, 'hour').toISOString(), status: 'confirmed', amenity: { name: 'SUM', deletedAt: null, amenityType: { icon: '🎉' }, building: { name: 'Torre Norte' } }, apartment: { name: '202' }, user: { name: 'María', surname: 'López' }, totalPrice: 0, currency: 'USD' },
    { id: '3', startDatetime: dayjs().add(3, 'day').toISOString(), endDatetime: dayjs().add(3, 'day').add(1, 'hour').toISOString(), status: 'pending', amenity: { name: 'Gimnasio', deletedAt: null, amenityType: { icon: '💪' }, building: { name: 'Edificio Sur' } }, apartment: { name: '305' }, user: { name: 'Carlos', surname: 'Martínez' }, totalPrice: 15, currency: 'USD' },
    { id: '4', startDatetime: dayjs().subtract(1, 'day').toISOString(), endDatetime: dayjs().subtract(1, 'day').add(1, 'hour').toISOString(), status: 'completed', amenity: { name: 'Pileta', deletedAt: null, amenityType: { icon: '🏊' }, building: { name: 'Torre Norte' } }, apartment: { name: '101' }, user: { name: 'Juan', surname: 'García' }, totalPrice: 25, currency: 'USD' },
    { id: '5', startDatetime: dayjs().subtract(2, 'day').toISOString(), endDatetime: dayjs().subtract(2, 'day').add(2, 'hour').toISOString(), status: 'cancelled', amenity: { name: 'SUM', deletedAt: null, amenityType: { icon: '🎉' }, building: { name: 'Edificio Sur' } }, apartment: { name: '410' }, user: { name: 'Ana', surname: 'Rodríguez' }, totalPrice: 0, currency: 'USD' },
    { id: '6', startDatetime: dayjs().subtract(5, 'day').toISOString(), endDatetime: dayjs().subtract(5, 'day').add(1, 'hour').toISOString(), status: 'completed', amenity: { name: 'Cancha de Tenis', deletedAt: null, amenityType: { icon: '🎾' }, building: { name: 'Torre Norte' } }, apartment: { name: '502' }, user: { name: 'Pedro', surname: 'Sánchez' }, totalPrice: 40, currency: 'USD' },
  ]

  const fetchReservations = ({ offset = 0, ...params } = {}) => {
    const query = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined)
    )
    // The API pages by page number, the table by offset.
    query.page = Math.floor(offset / (params.limit || 10)) + 1
    if (params.search) {
      query.userSearch = params.search
      delete query.search
    }
    setLastParams(query)
    methods.getReservations(query)
  }

  const columns = [
    {
      id: 'datetime',
      label: 'Fecha y Hora',
      minWidth: 160,
      format: (r) => (
        <div>
          <div>{dayjs(r.startDatetime).format('DD/MM/YYYY HH:mm')}</div>
          <div className={classes.subtle}>
            hasta {dayjs(r.endDatetime).format('HH:mm')}
          </div>
        </div>
      ),
    },
    {
      id: 'amenity',
      label: 'Amenity',
      minWidth: 160,
      format: (r) => {
        const deleted = r.amenity?.deletedAt != null
        return (
          <div className={deleted ? classes.deletedAmenity : ''}>
            <span style={{ marginRight: 6 }}>
              {r.amenity?.amenityType?.icon || '🏢'}
            </span>
            {r.amenity?.name || '(eliminada)'}
            {deleted && (
              <Chip
                label="Amenity eliminada"
                size="small"
                style={{ marginLeft: 6 }}
              />
            )}
          </div>
        )
      },
    },
    {
      id: 'building',
      label: 'Edificio',
      minWidth: 130,
      format: (r) => r.amenity?.building?.name || '—',
    },
    {
      id: 'apartment',
      label: 'Unidad',
      minWidth: 100,
      format: (r) => r.apartment?.name || '—',
    },
    {
      id: 'user',
      label: 'Persona',
      minWidth: 160,
      format: (r) =>
        r.user ? `${r.user.name || ''} ${r.user.surname || ''}`.trim() : '—',
    },
    {
      id: 'total',
      label: 'Total',
      minWidth: 100,
      format: (r) => {
        const t = Number(r.totalPrice || 0)
        return t > 0 ? `${r.currency || ''} ${t.toFixed(2)}` : '—'
      },
    },
    {
      id: 'status',
      label: 'Estado',
      minWidth: 110,
      format: (r) => STATUS_LABEL[r.status] || r.status,
    },
    { id: '', label: '', minWidth: 5 },
  ]

  const options = (reservation) => {
    if (reservation.status === 'cancelled' || isPast(reservation)) return []
    return [
      {
        key: 'cancel',
        label: 'Cancelar',
        onClick: () => {
          setPendingCancel(reservation)
          setCancelReason('')
        },
      },
    ]
  }

  const customFilters = [
    {
      type: 'select',
      field: 'amenityTypeId',
      label: 'Tipo de amenity',
      defaultValue: '',
      width: '280px',
      withRightMargin: true,
      options: [
        { value: '', label: 'Todos los tipos' },
        ...(amenityTypesState.amenityTypes || []).map((t) => ({
          value: t.id,
          label: t.name,
        })),
      ],
    },
  ]

  return (
    <>
      <ListHeader
        bigTitle="Reservas"
        canCreate={false}
        {...(showDevTools && {
          secondaryButtonText: '[DEV] Mock datos',
          secondaryButtonRedirect: () => setUseMock((v) => !v),
        })}
      />

      <CustomTable
        loading={reservationsState.isLoading}
        columns={columns}
        data={useMock ? MOCK_RESERVATIONS : (reservationsState.reservations || [])}
        options={options}
        serverPagination={true}
        serverCount={useMock ? MOCK_RESERVATIONS.length : reservationsState.pagination?.total}
        searchTitle="Persona"
        searchPlaceholder="Buscar por nombre o email"
        buildingFilter={true}
        customFilters={customFilters}
        getData={fetchReservations}
        getRowStyle={(r) => isPast(r) ? { opacity: 0.45 } : {}}
      />

      <DeleteConfirmationModal
        open={!!pendingCancel}
        onClose={() => setPendingCancel(null)}
        onDelete={async () => {
          try {
            await methods.cancelReservation(pendingCancel.id, cancelReason)
            setPendingCancel(null)
            setCancelReason('')
            methods.getReservations(lastParams)
          } catch (e) {
            // Hook handles toast.
          }
        }}
        text="¿Cancelar esta reserva? La persona será notificada."
      >
        <TextArea
          field={{ label: 'Motivo (opcional)', hint: 'Ingrese un motivo', key: 'reason' }}
          values={{ reason: cancelReason }}
          setValues={({ reason }) => setCancelReason(reason)}
          errors={{}}
        />
      </DeleteConfirmationModal>
    </>
  )
}

const useStyles = makeStyles(() => ({
  subtle: {
    color: '#7E8A99',
    fontSize: '0.8rem',
  },
  deletedAmenity: {
    color: '#7E8A99',
    fontStyle: 'italic',
  },
}))
